import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { PlatformMfaRenewal } from '@/components/legalControl/PlatformMfaRenewal'
import * as api from '@/services/legalControl/identityReview'

const labels: Record<api.Operation, string> = {
  merge: 'Merge identity groups',
  split: 'Split an identity group',
  dispute: 'Flag an ownership dispute',
  resolve_dispute: 'Resolve an ownership dispute',
  correction: 'Record an approved profile correction',
}

function Review({
  account,
  canApprove,
  canSubmit,
}: {
  account: string
  canApprove: boolean
  canSubmit: boolean
}) {
  const [initial] = useState(() => {
    try {
      return { intent: api.pending(account), error: '' }
    } catch {
      return {
        intent: null,
        error: 'Saved original intent is unavailable. Recover storage before writing.',
      }
    }
  })
  const [caseId, setCaseId] = useState(() => initial.intent?.case_id ?? crypto.randomUUID())
  const [reference, setReference] = useState(initial.intent?.case_id ?? '')
  const [clients, setClients] = useState(''),
    [operation, setOperation] = useState<api.Operation>('merge')
  const [partitions, setPartitions] = useState(''),
    [version, setVersion] = useState('')
  const [evidence, setEvidence] = useState(''),
    [sha, setSha] = useState(''),
    [decisionSha, setDecisionSha] = useState('')
  const [decision, setDecision] = useState<'approve' | 'reject'>('approve')
  const [context, setContext] = useState<api.Context | null>(null),
    [view, setView] = useState<api.Receipt | null>(null)
  const [intent, setIntent] = useState(initial.intent),
    [error, setError] = useState(initial.error),
    [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)
  const epoch = useRef(0)
  useEffect(
    () => () => {
      epoch.current++
    },
    []
  )
  async function run(work: () => Promise<void>) {
    const current = epoch.current
    setBusy(true)
    setError('')
    setMessage('')
    try {
      await work()
    } catch {
      if (epoch.current === current) {
        setView(null)
        setContext(null)
        setError(
          'Request unavailable. Keep the original case reference and recover its receipt before another command.'
        )
      }
    } finally {
      if (epoch.current === current) {
        setBusy(false)
        try {
          setIntent(api.pending(account))
        } catch {
          setError('Saved intent is unavailable. Recover storage before writing.')
        }
      }
    }
  }
  async function load(original: string) {
    const current = epoch.current,
      receipt = await api.inspect(original)
    if (current !== epoch.current) return
    const saved = api.pending(account)
    if (saved && saved.case_id !== receipt.case_id) throw new Error('Recover the saved case first')
    if (saved?.action === 'submit' && saved.command_sha256 !== receipt.command_sha256)
      throw new Error('Original command differs')
    if (!saved || saved.action === 'submit' || receipt.state !== 'pending') api.clear(account)
    setView(receipt)
    setCaseId(receipt.case_id)
    setReference(receipt.case_id)
    setContext(null)
    setMessage(
      receipt.state === 'pending'
        ? 'Original case recovered. An independent reviewer can decide it.'
        : 'Original terminal result recovered.'
    )
  }
  const locked = busy || Boolean(intent) || Boolean(initial.error) || !canSubmit
  return (
    <main className="p-6 space-y-5">
      <h1 className="text-2xl font-semibold">Identity adjudication</h1>
      <p>
        Review explicit client references and sealed evidence. Identity groups preserve each
        original record and its document access checks.
      </p>
      <PlatformMfaRenewal />
      <section className="card p-5 space-y-3" aria-label="Recover identity case">
        <label>
          Original case reference
          <input
            className="input w-full"
            value={reference}
            onChange={e => setReference(e.target.value)}
            disabled={busy}
          />
        </label>
        <button
          className="btn"
          disabled={busy || !reference || Boolean(initial.error)}
          onClick={() => void run(() => load(reference))}
        >
          Recover original case
        </button>
        {intent && (
          <p role="status">
            A saved {intent.action === 'decide' ? 'decision' : 'submission'} may have committed.
            Recover case {intent.case_id}. Exact decision retries must use the original evidence
            digest.
          </p>
        )}
      </section>
      {!view && canSubmit && (
        <section className="card p-5 space-y-3" aria-label="Prepare identity case">
          <p>Case reference: {caseId}</p>
          <label>
            Original client references
            <textarea
              className="input w-full"
              value={clients}
              disabled={locked}
              onChange={e => {
                setClients(e.target.value)
                setContext(null)
              }}
              placeholder="One original client UUID per line"
            />
          </label>
          <label>
            Adjudication
            <select
              className="input w-full"
              value={operation}
              disabled={locked}
              onChange={e => {
                setOperation(e.target.value as api.Operation)
                setContext(null)
              }}
            >
              {Object.entries(labels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <button
            className="btn"
            disabled={locked || !clients.trim()}
            onClick={() =>
              void run(async () => {
                const current = epoch.current,
                  value = await api.observe(caseId, api.identifiers(clients))
                if (epoch.current === current) setContext(value)
              })
            }
          >
            Inspect current identity context
          </button>
          {context && (
            <>
              <table className="w-full">
                <thead>
                  <tr>
                    <th>Original client</th>
                    <th>Kind</th>
                    <th>Status</th>
                    <th>Identity group</th>
                  </tr>
                </thead>
                <tbody>
                  {context.roots.map(r => (
                    <tr key={r.client_id}>
                      <td>{r.client_id}</td>
                      <td>{r.client_type}</td>
                      <td>{r.status}</td>
                      <td>{r.group_id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p>Complete group references: {context.required_client_ids.join(', ')}</p>
              {(operation === 'merge' || operation === 'split') && (
                <label>
                  Proposed groups
                  <textarea
                    className="input w-full"
                    value={partitions}
                    disabled={locked}
                    onChange={e => setPartitions(e.target.value)}
                    placeholder="One group per line; comma-separated original client references"
                  />
                </label>
              )}
              {operation === 'correction' && (
                <label>
                  Approved profile version reference
                  <input
                    className="input w-full"
                    value={version}
                    disabled={locked}
                    onChange={e => setVersion(e.target.value)}
                  />
                </label>
              )}
              <label>
                Sealed evidence reference
                <input
                  className="input w-full"
                  value={evidence}
                  disabled={locked}
                  onChange={e => setEvidence(e.target.value)}
                />
              </label>
              <label>
                Evidence SHA-256
                <input
                  className="input w-full"
                  value={sha}
                  disabled={locked}
                  onChange={e => setSha(e.target.value)}
                />
              </label>
              <p>
                This case is available for independent review for one hour. Merge and split require
                every member of each selected group.
              </p>
              <button
                className="btn"
                disabled={locked || !evidence || !sha}
                onClick={() =>
                  void run(async () => {
                    const body = api.submission({
                      case_id: caseId,
                      client_ids: context.roots.map(r => r.client_id),
                      operation,
                      partitions:
                        operation === 'merge' || operation === 'split'
                          ? partitions
                              .split('\n')
                              .filter(v => v.trim())
                              .map(api.identifiers)
                          : [],
                      expected_snapshot_sha256: context.snapshot_sha256,
                      evidence_reference: evidence,
                      evidence_sha256: sha,
                      profile_version_id: operation === 'correction' ? version : null,
                      expires_at: new Date(Date.now() + 3600_000).toISOString(),
                    })
                    setReference(caseId)
                    await api.submit(account, body)
                    await load(caseId)
                    setEvidence('')
                    setSha('')
                  })
                }
              >
                Submit original case
              </button>
            </>
          )}
        </section>
      )}
      {view && (
        <section className="card p-5 space-y-3" aria-label="Recorded identity case">
          <h2 className="font-semibold">{labels[view.command.operation]}</h2>
          <p>
            Case: {view.case_id}. State: {view.state}. Submitter: {view.submitter_user_id}.
          </p>
          <p>Original clients: {view.command.client_ids.join(', ')}</p>
          {view.command.partitions.map((group, i) => (
            <p key={i}>
              Proposed group {i + 1}: {group.join(', ')}
            </p>
          ))}
          {view.command.profile_version_id && (
            <p>Approved profile version: {view.command.profile_version_id}</p>
          )}
          <p>Sealed evidence reference: {view.command.evidence_reference}</p>
          {view.state === 'pending' && canApprove && view.submitter_user_id !== account && (
            <>
              <label>
                Decision
                <select
                  className="input w-full"
                  value={decision}
                  disabled={busy}
                  onChange={e => setDecision(e.target.value as 'approve' | 'reject')}
                >
                  <option value="approve">Approve</option>
                  <option value="reject">Reject</option>
                </select>
              </label>
              <label>
                Decision evidence SHA-256
                <input
                  className="input w-full"
                  value={decisionSha}
                  disabled={busy}
                  onChange={e => setDecisionSha(e.target.value)}
                />
              </label>
              <button
                className="btn"
                disabled={busy || !decisionSha}
                onClick={() =>
                  void run(async () => {
                    await api.decide(account, {
                      case_id: view.case_id,
                      decision,
                      expected_command_sha256: view.command_sha256,
                      evidence_sha256: decisionSha,
                    })
                    await load(view.case_id)
                    setDecisionSha('')
                  })
                }
              >
                {intent?.action === 'decide'
                  ? 'Retry exact original decision'
                  : 'Record independent decision'}
              </button>
            </>
          )}
          {view.state === 'pending' && view.submitter_user_id === account && (
            <p>A different reviewer must decide this case.</p>
          )}
          {!intent && view.state !== 'pending' && (
            <button
              className="btn"
              disabled={busy}
              onClick={() => {
                setView(null)
                setCaseId(crypto.randomUUID())
                setReference('')
                setClients('')
                setPartitions('')
                setEvidence('')
                setSha('')
              }}
            >
              Prepare another case
            </button>
          )}
        </section>
      )}
      {message && <p role="status">{message}</p>}
      {error && <p role="alert">{error}</p>}
    </main>
  )
}

export function IdentityReviewPage() {
  const { user } = useAuth()
  if (
    !user ||
    user.authScope !== 'platform' ||
    !['cml_admin', 'cml_operations'].includes(user.role) ||
    !user.permissions?.some(p =>
      [
        'txh.platform.client.profile.review.submit',
        'txh.platform.client.profile.review.approve',
      ].includes(p)
    )
  )
    return <p role="alert">Use a current platform identity reviewer session.</p>
  return (
    <Review
      key={[user.id, user.currentSessionId, user.permissions.join('|')].join(':')}
      account={user.id}
      canSubmit={user.permissions.includes('txh.platform.client.profile.review.submit')}
      canApprove={
        user.role === 'cml_admin' &&
        user.permissions.includes('txh.platform.client.profile.review.approve')
      }
    />
  )
}
